import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { Search, Trophy, LayoutGrid, BarChart3, Archive, CalendarDays, FolderOpen, CircleDot, ArrowRight, FileText, GraduationCap } from 'lucide-react';
import { useSearchableTasks, useSearchCalendarNotes } from '../../hooks/useSearch';
import { useAppStore } from '../../store';
import { useTutorialStore } from '../../store/tutorialStore';
import type { ViewType } from '../../store';
import type { Task } from '../../types';
import type { CalendarNote } from '../../types/calendar';

interface CommandPaletteProps {
  onClose: () => void;
}

type SearchResult =
  | { type: 'task'; task: Task }
  | { type: 'note'; note: CalendarNote }
  | { type: 'action'; label: string; icon: React.ReactNode; action: () => void; view: ViewType };

function getMatchSnippet(htmlContent: string, query: string): string | null {
  const text = htmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return null;
  const start = Math.max(0, idx - 40);
  const end = Math.min(text.length, idx + query.length + 40);
  let snippet = text.slice(start, end);
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';
  return snippet;
}

const STATUS_BADGE: Record<Task['status'], { label: string; className: string }> = {
  track: { label: 'Track', className: 'text-accent bg-accent/10' },
  backlog: { label: 'Backlog', className: 'text-text-muted bg-bg-raised' },
  done: { label: 'Done', className: 'text-finish bg-finish/10' },
  template: { label: 'Template', className: 'text-p2 bg-p2/10' },
};

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const allTasks = useSearchableTasks();
  const { data: matchingNotes } = useSearchCalendarNotes(query);
  const { setView, setSpaceId, setSelectedTaskId, setSelectedDate, setSelectedMeetingId, currentSpaceId, spaces } = useAppStore();
  const { replayTutorial } = useTutorialStore();

  const navActions = useMemo<SearchResult[]>(
    () => [
      {
        type: 'action',
        label: 'Go to Track',
        icon: <Trophy size={14} />,
        view: 'track' as const,
        action: () => { setView('track'); onClose(); },
      },
      {
        type: 'action',
        label: 'Go to Backlog',
        icon: <LayoutGrid size={14} />,
        view: 'backlog' as const,
        action: () => { setView('backlog'); onClose(); },
      },
      {
        type: 'action',
        label: 'Go to Summary',
        icon: <BarChart3 size={14} />,
        view: 'summary' as const,
        action: () => { setView('summary'); onClose(); },
      },
      {
        type: 'action',
        label: 'Go to Archive',
        icon: <Archive size={14} />,
        view: 'archive' as const,
        action: () => { setView('archive'); onClose(); },
      },
      {
        type: 'action',
        label: 'Go to Calendar',
        icon: <CalendarDays size={14} />,
        view: 'calendar' as const,
        action: () => { setView('calendar'); onClose(); },
      },
      {
        type: 'action',
        label: 'Replay tutorial',
        icon: <GraduationCap size={14} />,
        view: 'track' as const,
        action: () => { replayTutorial(); setView('track'); onClose(); },
      },
    ],
    [setView, onClose, replayTutorial],
  );

  const results = useMemo<SearchResult[]>(() => {
    if (!query.trim()) {
      return navActions;
    }

    const q = query.toLowerCase();

    const matchingTasks = allTasks
      .filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const aPrefix = a.title.toLowerCase().startsWith(q);
        const bPrefix = b.title.toLowerCase().startsWith(q);
        if (aPrefix && !bPrefix) return -1;
        if (!aPrefix && bPrefix) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      })
      .slice(0, 10)
      .map<SearchResult>((task) => ({ type: 'task', task }));

    const noteResults: SearchResult[] = (matchingNotes ?? [])
      .slice(0, 5)
      .map((note) => ({ type: 'note', note }));

    const matchingActions = navActions.filter((r) =>
      r.type === 'action' && r.label.toLowerCase().includes(q),
    );

    return [...matchingTasks, ...noteResults, ...matchingActions];
  }, [query, allTasks, matchingNotes, navActions]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, results.length]);

  useEffect(() => {
    document
      .getElementById(`palette-item-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function execute(result: SearchResult) {
    if (result.type === 'task') {
      const { task } = result;
      if (task.spaceId !== currentSpaceId) setSpaceId(task.spaceId);
      if (task.status === 'backlog') setView('backlog');
      else if (task.status === 'done') setView('archive');
      else setView('track');
      setSelectedTaskId(task.id);
      onClose();
    } else if (result.type === 'note') {
      setSelectedDate(result.note.date);
      setSelectedMeetingId(result.note.meetingKey);
      setView('calendar');
      onClose();
    } else {
      result.action();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(results.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
    } else if (e.key === 'Enter') {
      const active = results[activeIndex];
      if (active) execute(active);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  const listVariants: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.03 } },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, x: -8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.15, ease: [0.25, 1, 0.5, 1] } },
  };

  const taskResults = results.filter((r): r is Extract<SearchResult, { type: 'task' }> => r.type === 'task');
  const noteResults = results.filter((r): r is Extract<SearchResult, { type: 'note' }> => r.type === 'note');
  const actionResults = results.filter((r): r is Extract<SearchResult, { type: 'action' }> => r.type === 'action');

  // Map each result to its global index for keyboard navigation
  const taskStartIndex = 0;
  const noteStartIndex = taskResults.length;
  const actionStartIndex = taskResults.length + noteResults.length;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Command palette">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12 }}
        className="absolute inset-0 bg-bg-base/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative flex justify-center pt-[15vh] pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="surface-overlay surface-glow w-[min(540px,calc(100vw-2rem))] p-0 flex flex-col max-h-[70vh] pointer-events-auto"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
            <Search size={16} className="text-text-muted shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search tasks, notes, or type a command..."
              className="flex-1 bg-transparent text-body text-text-primary placeholder:text-text-muted focus:outline-none"
              aria-autocomplete="list"
              aria-controls="palette-results"
              aria-activedescendant={`palette-item-${activeIndex}`}
            />
            <kbd className="text-micro text-text-muted bg-bg-surface px-1.5 py-0.5 rounded border border-border-subtle">
              Esc
            </kbd>
          </div>

          {/* Results */}
          <div id="palette-results" role="listbox" className="flex-1 overflow-y-auto py-2">
            {/* Task results */}
            {taskResults.length > 0 && (
              <motion.div variants={listVariants} initial="hidden" animate="show">
                <p className="text-label text-text-muted px-4 py-1.5">Tasks</p>
                {taskResults.map((result, i) => {
                  const index = taskStartIndex + i;
                  const { task } = result;
                  const isProject = task.itemType === 'project';
                  const badge = STATUS_BADGE[task.status];
                  return (
                    <motion.button
                      variants={itemVariants}
                      key={task.id}
                      id={`palette-item-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onClick={() => execute(result)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        index === activeIndex
                          ? 'bg-accent/10 text-text-primary shadow-[inset_2px_0_0_var(--color-accent)]'
                          : 'text-text-secondary hover:bg-accent/5'
                      }`}
                    >
                      {/* Icon */}
                      <span className="shrink-0 text-text-muted">
                        {isProject ? <FolderOpen size={14} style={{ color: task.color ?? undefined }} /> : <CircleDot size={14} />}
                      </span>

                      {/* Title */}
                      <span className="text-caption font-medium flex-1 truncate">{task.title}</span>

                      {/* Badges */}
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className={`text-micro px-1.5 py-0.5 rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                        {task.spaceId !== currentSpaceId && (
                          <span className="text-micro px-1.5 py-0.5 rounded-full text-text-muted bg-bg-raised capitalize">
                            {spaces.find((s) => s.id === task.spaceId)?.name ?? 'Other'}
                          </span>
                        )}
                      </span>

                      <ArrowRight size={12} className="text-text-muted shrink-0" />
                    </motion.button>
                  );
                })}
              </motion.div>
            )}

            {/* Note results */}
            {noteResults.length > 0 && (
              <motion.div variants={listVariants} initial="hidden" animate="show" className={taskResults.length > 0 ? 'mt-1' : ''}>
                <p className="text-label text-text-muted px-4 py-1.5">Meeting Notes</p>
                {noteResults.map((result, i) => {
                  const index = noteStartIndex + i;
                  const { note } = result;
                  const snippet = getMatchSnippet(note.content, query);
                  return (
                    <motion.button
                      variants={itemVariants}
                      key={note.id}
                      id={`palette-item-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onClick={() => execute(result)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        index === activeIndex
                          ? 'bg-accent/10 text-text-primary shadow-[inset_2px_0_0_var(--color-accent)]'
                          : 'text-text-secondary hover:bg-accent/5'
                      }`}
                    >
                      <span className="shrink-0 text-text-muted self-start mt-0.5">
                        <FileText size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-caption font-medium truncate block">{note.title}</span>
                        {snippet && (
                          <span className="text-micro text-text-muted truncate block mt-0.5">{snippet}</span>
                        )}
                      </div>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-micro px-1.5 py-0.5 rounded-full text-text-muted bg-bg-raised">
                          {note.date}
                        </span>
                      </span>
                      <ArrowRight size={12} className="text-text-muted shrink-0" />
                    </motion.button>
                  );
                })}
              </motion.div>
            )}

            {/* Nav action results */}
            {actionResults.length > 0 && (
              <motion.div variants={listVariants} initial="hidden" animate="show" className={(taskResults.length > 0 || noteResults.length > 0) ? 'mt-1' : ''}>
                <p className="text-label text-text-muted px-4 py-1.5">Navigate</p>
                {actionResults.map((result, i) => {
                  const index = actionStartIndex + i;
                  return (
                    <motion.button
                      variants={itemVariants}
                      key={result.label}
                      id={`palette-item-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onClick={() => execute(result)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        index === activeIndex
                          ? 'bg-accent/10 text-text-primary shadow-[inset_2px_0_0_var(--color-accent)]'
                          : 'text-text-secondary hover:bg-accent/5'
                      }`}
                    >
                      <span className="shrink-0 text-text-muted">{result.icon}</span>
                      <span className="text-caption flex-1">{result.label}</span>
                      <ArrowRight size={12} className="text-text-muted shrink-0" />
                    </motion.button>
                  );
                })}
              </motion.div>
            )}

            {/* Empty state */}
            {results.length === 0 && query && (
              <p className="text-caption text-text-muted text-center py-8">
                No results for &ldquo;{query}&rdquo;
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-border-subtle">
            <span className="text-micro text-text-muted" role="status" aria-live="polite">
              {taskResults.length} task{taskResults.length !== 1 ? 's' : ''}
              {noteResults.length > 0 && `, ${noteResults.length} note${noteResults.length !== 1 ? 's' : ''}`}
            </span>
            <div className="flex items-center gap-3 text-micro text-text-muted">
              <span>↑↓ navigate</span>
              <span>↵ select</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
